// =====================================================================
// aiErrors — shared helper for surfacing BYOK / cap-exceeded errors from
// the AI edge functions (content-plan-brief, content-draft-brief).
// The router returns:
//   { error: string, code: "byok_required" | "cap_exceeded" | "kill_switch"
//                          | "missing_admin_key" | "no_ai_access" | "internal" }
// with HTTP 402 / 403 / 500 depending on the case.
//
// supabase-js wraps non-2xx responses in a FunctionsHttpError whose
// `context` carries the original Response. We parse it back out and toast
// a useful message (with a Settings link for the BYOK cases).
// =====================================================================

import { toast } from "sonner";

type AiErrorBody = {
  error?: string;
  code?:
    | "byok_required"
    | "cap_exceeded"
    | "kill_switch"
    | "missing_admin_key"
    | "no_ai_access"
    | "starter_quota_exceeded"
    | "premium_writing_disabled"
    | "internal";
};

const SETTINGS_PATH = "/settings";
const PRICING_PATH = "/pricing";

const tryParseAiError = async (err: unknown): Promise<AiErrorBody | null> => {
  if (!err || typeof err !== "object") return null;
  // FunctionsHttpError exposes `context: Response`.
  const ctx = (err as { context?: unknown }).context;
  if (ctx && typeof ctx === "object" && "json" in (ctx as Record<string, unknown>)) {
    try {
      const body = await (ctx as Response).clone().json();
      if (body && typeof body === "object") return body as AiErrorBody;
    } catch {
      // fall through
    }
  }
  return null;
};

/**
 * Inspect an error from `supabase.functions.invoke` against an AI edge
 * function. If it's a recognized router error code, show a targeted toast
 * with the right call-to-action and return `true` (caller should NOT show
 * a generic toast). Returns `false` otherwise — caller should fall back
 * to its existing generic error handling.
 */
export const handleAiInvocationError = async (err: unknown): Promise<boolean> => {
  const body = await tryParseAiError(err);
  if (!body?.code) return false;

  switch (body.code) {
    case "byok_required":
      toast.error("OpenRouter key required", {
        description:
          "Your plan requires your own OpenRouter key for AI Chat, the Planning Agent, and the Writing Agent. Add one in Settings → AI providers.",
        action: { label: "Open settings", onClick: () => { window.location.href = SETTINGS_PATH; } },
      });
      return true;
    case "cap_exceeded":
      toast.error("Shared AI capacity reached", {
        description:
          "Your account has hit this month's shared OpenRouter cap for AI Chat, Planning, and Writing. Add your own OpenRouter key in Settings to keep going.",
        action: { label: "Open settings", onClick: () => { window.location.href = SETTINGS_PATH; } },
      });
      return true;
    case "starter_quota_exceeded":
      toast.error("You're out of AI credits this month", {
        description:
          body.error ??
          "Buy a credit top-up in Settings to keep going, or upgrade to Plus for a larger monthly allowance.",
        action: { label: "Buy credits", onClick: () => { window.location.href = `${SETTINGS_PATH}?buy_credits=1`; } },
      });
      return true;
    case "premium_writing_disabled":
      toast.error("AI Chat, Planning, and Writing temporarily paused", {
        description:
          body.error ??
          "The shared OpenRouter pool is paused for your plan right now. Add your own OpenRouter key in Settings to bypass this.",
        action: { label: "Open settings", onClick: () => { window.location.href = SETTINGS_PATH; } },
      });
      return true;
    case "no_ai_access":
      // Starter Writing-Agent path: offer BOTH "Add OpenRouter key" and
      // "See plans" so the user can pick the rail they want. Other tiers
      // hitting no_ai_access just see "See plans".
      if (body.error?.includes("Starter plan")) {
        toast.error("Writing Agent requires a key or upgrade", {
          description: body.error,
          action: { label: "Add key", onClick: () => { window.location.href = SETTINGS_PATH; } },
          cancel: { label: "See plans", onClick: () => { window.location.href = PRICING_PATH; } },
        });
        return true;
      }
      toast.error("Plan upgrade required", {
        description: body.error ?? "AI generation isn't included on your current plan.",
        action: { label: "See plans", onClick: () => { window.location.href = PRICING_PATH; } },
      });
      return true;
    case "kill_switch":
    case "missing_admin_key":
      toast.error("AI writing temporarily unavailable", {
        description:
          body.error ?? "The shared writing pool is paused right now. Try again in a few minutes.",
      });
      return true;
    case "internal":
      toast.error("AI writing failed", {
        description: body.error ?? "Something went wrong on our side. Try again.",
      });
      return true;
    default:
      return false;
  }
};
